import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { NumberInput } from "../../ui/number-input";
import {
  buildMaterialClassificationOptions,
  FINISH_MODE_OPTIONS,
} from "../../../utils/materialFlow";
import {
  COMPOSER_PROGRESS_STEPS,
  COMPOSER_STEP_INTERVAL_MS,
} from "./traceLabels";
import { draftLineItem as draftLineItemRequest } from "../../../services/apiService";

/**
 * "Draft with AI" — describe the thing, get a reviewable line back.
 *
 * One component, three states, only ever one on screen:
 *
 *   form     what the estimator would tell a lead man: a description, and
 *            whatever dimensions and finish they already know.
 *   working  the request takes 5-20 seconds. A half-disabled form during that
 *            would invite a second submit, so the form is replaced outright by
 *            a progress state with a real Cancel.
 *   error    the backend's own sentence, plus the shop rules it broke.
 *
 * Nothing here prices anything. On success the draft lands as an editable line
 * that is held out of the calculation payload entirely until the estimator
 * accepts it; from then on it prices exactly like a line typed by hand.
 */

const PLACEHOLDER = '8 craftsman rafter tails, 6" x 24", AZEK, painted';

function ProgressState({ elapsedSeconds, stepIndex, onCancel }) {
  return (
    <div className="est-composer__working">
      <div
        className="est-composer__bar"
        role="progressbar"
        aria-label="Drafting the line item"
      >
        <span className="est-composer__bar-fill" />
      </div>
      <p className="est-composer__step" role="status">
        {COMPOSER_PROGRESS_STEPS[stepIndex]}
      </p>
      <p className="est-composer__elapsed">
        {elapsedSeconds}s · usually 5-20 seconds
      </p>
      <div className="est-composer__actions">
        <button type="button" className="wb-tbtn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ErrorState({ error, onRetry, onClose }) {
  return (
    <div className="est-composer__error">
      <p className="est-alert" role="alert">
        {error.message}
      </p>
      {error.violations?.length ? (
        <ul className="est-composer__violations">
          {error.violations.map((violation) => (
            <li key={violation}>{violation}</li>
          ))}
        </ul>
      ) : null}
      <div className="est-composer__actions">
        <button type="button" className="wb-tbtn" onClick={onClose}>
          Close
        </button>
        <button
          type="button"
          className="wb-tbtn wb-tbtn--solid"
          onClick={onRetry}
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function ComposerDialog({
  open,
  onOpenChange,
  onDrafted,
  availableMaterials = [],
  onUnavailable,
  draftLineItem = draftLineItemRequest,
}) {
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [length, setLength] = useState(null);
  const [width, setWidth] = useState(null);
  const [height, setHeight] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [finishMode, setFinishMode] = useState("");
  const [materialClass, setMaterialClass] = useState("");

  const [phase, setPhase] = useState("form");
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);

  const abortRef = useRef(null);

  const classOptions = buildMaterialClassificationOptions(
    availableMaterials || [],
  );

  // A dialog closed mid-request must not leave the request running, and a
  // component unmounted mid-request must not resolve into dead state.
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    if (phase !== "working") return undefined;
    setElapsed(0);
    setStepIndex(0);
    const tick = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    const step = window.setInterval(
      () =>
        setStepIndex((index) =>
          Math.min(index + 1, COMPOSER_PROGRESS_STEPS.length - 1),
        ),
      COMPOSER_STEP_INTERVAL_MS,
    );
    return () => {
      window.clearInterval(tick);
      window.clearInterval(step);
    };
  }, [phase]);

  const canSubmit = description.trim().length >= 3;

  const closeDialog = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPhase("form");
    setError(null);
    onOpenChange?.(false);
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPhase("form");
  };

  const submit = async (event) => {
    event?.preventDefault?.();
    if (!canSubmit || phase === "working") return;

    const controller = new AbortController();
    abortRef.current = controller;
    setPhase("working");
    setError(null);

    const dimensions =
      length === null && width === null && height === null
        ? undefined
        : { length_in: length, width_in: width, height_in: height };

    try {
      const payload = await draftLineItem(
        {
          description: description.trim(),
          dimensions,
          quantity: quantity === null ? 1 : quantity,
          finish_mode: finishMode || null,
          material_classification: materialClass || null,
          notes: notes.trim() || null,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      abortRef.current = null;
      onDrafted?.(payload);
      setPhase("form");
      setDescription("");
      setNotes("");
      onOpenChange?.(false);
    } catch (requestError) {
      if (controller.signal.aborted) return;
      abortRef.current = null;
      // The server no longer has the composer — hide it rather than letting
      // the estimator keep hitting a button that cannot work.
      if (requestError?.status === 503) {
        onUnavailable?.();
        onOpenChange?.(false);
        setPhase("form");
        return;
      }
      setError(requestError);
      setPhase("error");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange?.(true) : closeDialog())}
    >
      <DialogContent className="est-composer">
        <DialogHeader>
          <DialogTitle>Draft a line with AI</DialogTitle>
          <DialogDescription>
            Describe the piece the way you would to a lead man. You get an
            editable draft line, left out of the price until you accept it.
          </DialogDescription>
        </DialogHeader>

        {phase === "working" ? (
          <ProgressState
            elapsedSeconds={elapsed}
            stepIndex={stepIndex}
            onCancel={handleCancel}
          />
        ) : phase === "error" ? (
          <ErrorState
            error={error || { message: "The draft failed." }}
            onRetry={() => setPhase("form")}
            onClose={closeDialog}
          />
        ) : (
          <form className="est-composer__form" onSubmit={submit} noValidate>
            <div>
              <label htmlFor="composer-description">
                What are we building?
              </label>
              <textarea
                id="composer-description"
                className="wb-input est-composer__text"
                rows={3}
                autoFocus
                placeholder={PLACEHOLDER}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>

            <div className="est-fields">
              <div>
                <label htmlFor="composer-length">Length</label>
                <div className="est-suffix">
                  <NumberInput
                    id="composer-length"
                    align="right"
                    step="0.5"
                    value={length ?? ""}
                    onValueChange={setLength}
                    className="h-9"
                  />
                  <span>in</span>
                </div>
              </div>
              <div>
                <label htmlFor="composer-width">Width</label>
                <div className="est-suffix">
                  <NumberInput
                    id="composer-width"
                    align="right"
                    step="0.5"
                    value={width ?? ""}
                    onValueChange={setWidth}
                    className="h-9"
                  />
                  <span>in</span>
                </div>
              </div>
              <div>
                <label htmlFor="composer-height">Height</label>
                <div className="est-suffix">
                  <NumberInput
                    id="composer-height"
                    align="right"
                    step="0.5"
                    value={height ?? ""}
                    onValueChange={setHeight}
                    className="h-9"
                  />
                  <span>in</span>
                </div>
              </div>
              <div>
                <label htmlFor="composer-quantity">Quantity</label>
                <div className="est-suffix">
                  <NumberInput
                    id="composer-quantity"
                    align="right"
                    step="1"
                    value={quantity ?? ""}
                    onValueChange={setQuantity}
                    className="h-9"
                  />
                  <span>ea</span>
                </div>
              </div>
            </div>

            <div className="est-fields">
              <div>
                <label htmlFor="composer-finish">Finish</label>
                <select
                  id="composer-finish"
                  className="wb-input"
                  value={finishMode}
                  onChange={(event) => setFinishMode(event.target.value)}
                >
                  <option value="">Not sure yet</option>
                  {FINISH_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="composer-class">Material class</label>
                <select
                  id="composer-class"
                  className="wb-input"
                  value={materialClass}
                  onChange={(event) => setMaterialClass(event.target.value)}
                >
                  <option value="">Let the draft choose</option>
                  {classOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="composer-notes">Notes (optional)</label>
              <textarea
                id="composer-notes"
                className="wb-input est-composer__text"
                rows={2}
                placeholder="Anything unusual — site conditions, a vendor profile, a past job to match"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </div>

            <div className="est-composer__actions">
              <button type="button" className="wb-tbtn" onClick={closeDialog}>
                Cancel
              </button>
              <button
                type="submit"
                className="wb-tbtn wb-tbtn--solid"
                disabled={!canSubmit}
              >
                Draft the line
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ComposerDialog;
